import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { FaPencilAlt, FaCamera, FaTimes } from 'react-icons/fa';
import ProfileHighlight from './ProfileHighlight';
import Alerty from '../../common/Alerty';

const { rootUrl } = require('../../../config/config');

const ProfileEdit = () => {

  const { user, updateUserProfile } = useAuth();
  const token = localStorage.getItem('authToken');
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState({});
  const [inAlert, setInAlert] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [email2FAEnabled, setEmail2FAEnabled] = useState(false);
  const fileInputRef = useRef(null);


  const [formData, setFormData] = useState({
    name: user?.name || '',
    username: user?.username || '',
    email: user?.email || '',
    password: '',
  });


  useEffect(() => {
    setFormData({
      name: user?.name || '',
      username: user?.username || '',
      email: user?.email || '',
      password: '',
    });
  }, [user]);

  useEffect(() => {
    const fetchProfileSettings = async () => {
      if (!token) {
        return;
      }

      try {
        const response = await axios.get(`${rootUrl}/api/users/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data?.success && response.data.data) {
          setEmail2FAEnabled(response.data.data.email_2fa_enabled === true);
        }
      } catch (error) {
        console.error('Error fetching profile settings:', error);
      }
    };

    fetchProfileSettings();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setAlertMessage({ color: 'red', message: 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.' });
        setInAlert(true);
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setAlertMessage({ color: 'red', message: 'File size too large. Please upload an image smaller than 5MB.' });
        setInAlert(true);
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPicture = async () => {
    if (!fileInputRef.current?.files[0]) {
      setAlertMessage({ color: 'red', message: 'Please select an image to upload.' });
      setInAlert(true);
      return;
    }

    setUploadingPicture(true);
    setInAlert(false);

    try {
      const formData = new FormData();
      formData.append('profilePicture', fileInputRef.current.files[0]);

      const response = await axios.post(
        `${rootUrl}/api/users/profile/picture`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (response.data.success) {
        // Update user profile in context
        updateUserProfile({ profile_picture: response.data.data.profile_picture });
        setAlertMessage({ color: 'green', message: 'Profile picture uploaded successfully!' });
        setInAlert(true);
        setPreviewImage(null);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      setAlertMessage({
        color: 'red',
        message: error.response?.data?.message || 'Failed to upload profile picture. Please try again.'
      });
      setInAlert(true);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleRemovePicture = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    setUploadingPicture(true);
    setInAlert(false);

    try {
      const response = await axios.delete(
        `${rootUrl}/api/users/profile/picture`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Update user profile in context
        updateUserProfile({ profile_picture: null });
        setAlertMessage({ color: 'green', message: 'Profile picture removed successfully!' });
        setInAlert(true);
        setPreviewImage(null);
        localStorage.removeItem('cfms-profile-picture');
      }
    } catch (error) {
      setAlertMessage({
        color: 'red',
        message: error.response?.data?.message || 'Failed to remove profile picture. Please try again.'
      });
      setInAlert(true);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSaveSettings = async () => {
    // reset any existing alert
    setInAlert(false)

    // simple validation
    if (!formData.name.trim()) {
      setAlertMessage({ color: 'red', message: 'Name is required.' })
      setInAlert(true)
      return
    }

    setIsLoading(true)
    try {
      // build payload and call server
      const payload = {
        name: formData.name,
        username: formData.username,
        email: formData.email,
        ...(formData.password && { password: formData.password }),
      }

      await axios.put(
        `${rootUrl}/edit/${user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(prev => ({ ...prev, password: '' }));

      // success feedback
      setAlertMessage({ color: 'green', message: 'Settings updated successfully.' });
      setInAlert(true);
    } catch (error) {
      // error feedback
      setAlertMessage({ color: 'red', message: 'Failed to update settings. Please try again.' })
      setInAlert(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleEmail2FA = async () => {
    setInAlert(false);

    try {
      const response = await axios.put(
        `${rootUrl}/api/users/profile/2fa`,
        { enabled: !email2FAEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        const nextValue = response.data.data.email_2fa_enabled === true;
        setEmail2FAEnabled(nextValue);
        setAlertMessage({
          color: 'green',
          message: `Email 2FA ${nextValue ? 'enabled' : 'disabled'} successfully.`
        });
        setInAlert(true);
      }
    } catch (error) {
      setAlertMessage({
        color: 'red',
        message: error.response?.data?.message || 'Failed to update email 2FA setting.'
      });
      setInAlert(true);
    }
  };

  return (
    <>

      <div className="w-full md:w-1/2 mx-2 mb-6 flex flex-wrap">


        <ProfileHighlight formData={formData} />

        {/* Profile Picture Upload Section */}
        <div className="w-full mb-8 bg-white rounded-lg p-6 border border-[#EDE4DA]">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaCamera className="inline-block mr-2 text-gray-800" />
            Profile Picture
          </h2>

          {alertMessage && alertMessage.message && alertMessage.color && (
            <div className="mb-4">
              <Alerty
                open={inAlert}
                setOpen={setInAlert}
                color={alertMessage.color}
                message={alertMessage.message}
              />
            </div>
          )}

          <div className="flex flex-col items-center space-y-4">
            {/* Current/Preview Profile Picture */}
            <div className="relative">
              {(previewImage || user?.profile_picture) ? (
                <img
                  src={previewImage || `${rootUrl}${user.profile_picture}`}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-indigo-500"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300">
                  <FaCamera className="text-4xl text-gray-400" />
                </div>
              )}
            </div>

            {/* File Input */}
            <div className="flex flex-col items-center space-y-3 w-full">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
                id="profile-picture-input"
              />
              <label
                htmlFor="profile-picture-input"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 cursor-pointer transition-colors"
              >
                <FaCamera className="inline-block mr-2" />
                {previewImage ? 'Change Picture' : 'Upload Picture'}
              </label>

              {previewImage && (
                <div className="flex space-x-2">
                  <button
                    onClick={handleUploadPicture}
                    disabled={uploadingPicture}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {uploadingPicture ? 'Uploading...' : 'Save Picture'}
                  </button>
                  <button
                    onClick={() => {
                      setPreviewImage(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {user?.profile_picture && !previewImage && (
                <button
                  onClick={handleRemovePicture}
                  disabled={uploadingPicture}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <FaTimes className="inline-block mr-2" />
                  {uploadingPicture ? 'Removing...' : 'Remove Picture'}
                </button>
              )}
            </div>

            <p className="text-sm text-gray-500 text-center">
              Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
            </p>
          </div>
        </div>

        <div className="w-full mb-8 bg-white rounded-lg p-6 border border-[#EDE4DA]">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaPencilAlt className="inline-block mr-2 text-gray-800" />
            Login Security
          </h2>

          <div className="flex items-center justify-between border border-gray-200 rounded-sm px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-700">Email 2FA</p>
              <p className="text-xs text-gray-500">Receive a verification code on login</p>
            </div>
            <button
              type="button"
              onClick={handleToggleEmail2FA}
              className={`px-3 py-1 rounded-sm text-xs font-semibold ${email2FAEnabled
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700'
                }`}
            >
              {email2FAEnabled ? 'Enabled' : 'Off'}
            </button>
          </div>
        </div>

        <div className="w-full mb-8 bg-white rounded-lg p-6 border border-[#EDE4DA]">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaPencilAlt className="inline-block mr-2 text-gray-800" />
            Edit Profile
          </h2>

          {alertMessage && alertMessage.message && alertMessage.color && (
            <div>
              <Alerty
                open={inAlert}
                setOpen={setInAlert}
                color={alertMessage.color}
                message={alertMessage.message}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                className="block w-full p-3 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                readOnly
                className="block w-full p-3 border border-gray-200 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                className="block w-full p-3 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                className="block w-full p-3 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-6">

            <button
              onClick={handleSaveSettings}
              disabled={isLoading}
              className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

      </div>
    </>
  );
};

export default ProfileEdit;
